import { type ReactNode, useId } from "react";

import { PencilSimpleIcon, XCircleIcon } from "@phosphor-icons/react";
import {
  Badge,
  Checkbox,
  Flex,
  IconButton,
  Text,
  Tooltip,
  VisuallyHidden,
} from "@radix-ui/themes";

import type { DataTableColumns } from "~/v3-poc/components/DataTable";
import type { OrdersWidgetRow } from "./OrdersWidget.types";
import {
  compareOrdersNumericValues,
  compareOrdersTextValues,
  compareOrdersTimestampValues,
  ORDERS_UNAVAILABLE_VALUE,
} from "./OrdersWidget.utils";

export type OrdersWidgetColumnId = Exclude<
  keyof OrdersWidgetRow,
  | "id"
  | "sideColor"
  | "statusColor"
  | "canCancel"
  | "canReplace"
  | "canEdit"
  | "editDisabledReason"
  | "cancelDisabledReason"
  | "isEditing"
  | "isCancelling"
  | "isLegRow"
  | "ratioLabel"
  | "parentOrderId"
  | "submittedAtValue"
  | "createdAtValue"
  | "startTimeValue"
  | "endTimeValue"
  | "legCount"
  | "legs"
  | "rawSymbol"
>;

export interface OrdersWidgetColumnOption {
  id: OrdersWidgetColumnId;
  label: string;
}

export const ORDERS_WIDGET_COLUMN_OPTIONS = [
  { id: "symbol", label: "Symbol" },
  { id: "name", label: "Name" },
  { id: "submittedAt", label: "Submitted At" },
  { id: "createdAt", label: "Created At" },
  { id: "side", label: "Side" },
  { id: "orderType", label: "Type" },
  { id: "quantity", label: "Qty" },
  { id: "filledQuantity", label: "Filled" },
  { id: "limitPrice", label: "Limit" },
  { id: "status", label: "Status" },
  { id: "action", label: "Actions" },
  { id: "stopPrice", label: "Stop Price" },
  { id: "trailPrice", label: "Trail Price" },
  { id: "trailPercent", label: "Trail Percent" },
  { id: "extendedHours", label: "Extended Hours" },
  { id: "clientOrderId", label: "Client Order ID" },
  { id: "orderClass", label: "Order Class" },
  { id: "algorithm", label: "Algorithm" },
  { id: "destination", label: "Destination" },
  { id: "startTime", label: "Start Time" },
  { id: "endTime", label: "End Time" },
  { id: "maxPercentage", label: "Max Percentage" },
  { id: "notional", label: "Notional" },
  { id: "timeInForce", label: "Time-in-Force" },
  { id: "filledAtAveragePrice", label: "Filled at Avg Price" },
  { id: "source", label: "Source" },
  { id: "positionIntent", label: "Position Intent" },
  { id: "markets", label: "Markets" },
  { id: "currency", label: "Currency" },
] as const satisfies readonly OrdersWidgetColumnOption[];

export const ORDERS_WIDGET_COLUMN_IDS: readonly OrdersWidgetColumnId[] =
  ORDERS_WIDGET_COLUMN_OPTIONS.map(({ id }) => id);

/** Blotter columns under advanced_instructions — gated by C19 persona. */
export const ADVANCED_INSTRUCTIONS_COLUMN_IDS = [
  "algorithm",
  "startTime",
  "endTime",
  "maxPercentage",
] as const satisfies readonly OrdersWidgetColumnId[];

const ADVANCED_INSTRUCTIONS_COLUMN_ID_SET = new Set<string>(
  ADVANCED_INSTRUCTIONS_COLUMN_IDS,
);

export const isAdvancedInstructionsColumnId = (
  columnId: string,
): columnId is (typeof ADVANCED_INSTRUCTIONS_COLUMN_IDS)[number] =>
  ADVANCED_INSTRUCTIONS_COLUMN_ID_SET.has(columnId);

export const getOrdersWidgetColumnOptions = (
  includeAction: boolean,
  allowAdvancedInstructions: boolean = true,
): readonly OrdersWidgetColumnOption[] =>
  ORDERS_WIDGET_COLUMN_OPTIONS.filter(({ id }) => {
    if (!includeAction && id === "action") {
      return false;
    }
    if (!allowAdvancedInstructions && isAdvancedInstructionsColumnId(id)) {
      return false;
    }
    return true;
  });

export const getOrdersWidgetAvailableColumnIds = (
  includeAction: boolean,
  allowAdvancedInstructions: boolean = true,
): readonly OrdersWidgetColumnId[] =>
  getOrdersWidgetColumnOptions(includeAction, allowAdvancedInstructions).map(
    ({ id }) => id,
  );

export const DEFAULT_ORDERS_WIDGET_VISIBLE_COLUMN_IDS = [
  "symbol",
  "name",
  "submittedAt",
  "side",
  "orderType",
  "quantity",
  "filledQuantity",
  "limitPrice",
  "status",
] as const satisfies readonly OrdersWidgetColumnId[];

const ORDERS_WIDGET_COLUMN_ID_SET = new Set<string>(ORDERS_WIDGET_COLUMN_IDS);

export const isOrdersWidgetColumnId = (
  value: unknown,
): value is OrdersWidgetColumnId =>
  typeof value === "string" && ORDERS_WIDGET_COLUMN_ID_SET.has(value);

interface TextValueProps {
  value: string;
  label: string;
  weight?: "medium" | "bold";
  color?: "gray";
  size?: "1" | "2";
}

const TextValue = ({ value, label, weight, color, size }: TextValueProps) =>
  value === ORDERS_UNAVAILABLE_VALUE ? (
    <Text color="gray" size={size} aria-label={`${label} unavailable`}>
      {ORDERS_UNAVAILABLE_VALUE}
    </Text>
  ) : (
    <Text weight={weight} color={color} size={size}>
      {value}
    </Text>
  );

type OrdersColumn = DataTableColumns<OrdersWidgetRow>[number];
type OrdersColumnMeta = NonNullable<OrdersColumn["meta"]>;
type TextColumnOptions = Pick<TextValueProps, "weight" | "color" | "size"> &
  Partial<OrdersColumn>;

const NUMERIC_SORT_COLUMN_IDS = new Set<string>([
  "quantity",
  "filledQuantity",
  "limitPrice",
  "stopPrice",
  "trailPrice",
  "trailPercent",
  "maxPercentage",
  "notional",
  "filledAtAveragePrice",
]);

const TIMESTAMP_SORT_VALUES: Record<string, (row: OrdersWidgetRow) => string> =
  {
    submittedAt: (row) => row.submittedAtValue,
    createdAt: (row) => row.createdAtValue,
    startTime: (row) => row.startTimeValue,
    endTime: (row) => row.endTimeValue,
  };

const compareOrdersColumn: NonNullable<OrdersColumn["sortFn"]> = (
  rowA,
  rowB,
  columnId,
) => {
  const descending =
    rowA
      .getAllCells()
      .find((cell) => cell.column.id === columnId)
      ?.column.getIsSorted() === "desc";
  const getTimestamp = TIMESTAMP_SORT_VALUES[columnId];
  if (getTimestamp) {
    const timeDelta = compareOrdersTimestampValues(
      getTimestamp(rowA.original),
      getTimestamp(rowB.original),
      descending,
    );
    if (timeDelta !== 0) {
      return timeDelta;
    }
    if (rowA.original.parentOrderId !== rowB.original.parentOrderId) {
      return rowA.original.parentOrderId < rowB.original.parentOrderId ? -1 : 1;
    }
    return 0;
  }

  const left = String(rowA.getValue(columnId) ?? "");
  const right = String(rowB.getValue(columnId) ?? "");
  return NUMERIC_SORT_COLUMN_IDS.has(columnId)
    ? compareOrdersNumericValues(left, right, descending)
    : compareOrdersTextValues(left, right, descending);
};

const createTextColumn = (
  accessorKey: OrdersWidgetColumnId,
  header: string,
  label: string,
  meta: OrdersColumnMeta,
  options: TextColumnOptions = {},
): OrdersColumn => {
  const { weight, color, size, ...extra } = options;
  return {
    accessorKey,
    header,
    cell: (info) => (
      <TextValue
        value={info.getValue<string>()}
        label={label}
        weight={weight}
        color={color}
        size={size}
      />
    ),
    enableSorting: true,
    sortFn: compareOrdersColumn,
    meta,
    ...extra,
  };
};

export interface OrdersWidgetColumnCallbacks {
  onCancel?: (orderId: string) => void;
  onEdit?: (orderId: string, origin: HTMLButtonElement) => void;
  selection?: {
    selectedIds: ReadonlySet<string>;
    onToggle: (orderId: string, selected: boolean) => void;
    allSelected?: boolean;
    someSelected?: boolean;
    onToggleAll?: (selected: boolean) => void;
  };
}

const SELECT_COLUMN_WIDTH = "2.5rem";

const createSelectionColumn = (
  selection: NonNullable<OrdersWidgetColumnCallbacks["selection"]>,
): OrdersColumn => ({
  id: "select",
  header: () => (
    <Flex align="center" justify="center">
      {selection.onToggleAll ? (
        <Checkbox
          checked={
            selection.allSelected
              ? true
              : selection.someSelected
                ? "indeterminate"
                : false
          }
          aria-label="Select all open orders"
          onCheckedChange={(checked) =>
            selection.onToggleAll?.(checked === true)
          }
        />
      ) : (
        <VisuallyHidden>Select orders to cancel</VisuallyHidden>
      )}
    </Flex>
  ),
  enableSorting: false,
  cell: ({ row }) => {
    if (row.original.isLegRow) {
      return null;
    }

    const disabledReason = getCancelDisabledReason(row.original);

    return (
      <Flex align="center" justify="center">
        <Checkbox
          checked={selection.selectedIds.has(row.original.id)}
          disabled={Boolean(disabledReason)}
          aria-label={`Select ${row.original.symbol} order ${row.original.id}`}
          onCheckedChange={(checked) =>
            selection.onToggle(row.original.id, checked === true)
          }
        />
      </Flex>
    );
  },
  meta: {
    align: "center",
    width: SELECT_COLUMN_WIDTH,
    sticky: "start",
    stickyOffset: "0",
    reorderable: false,
    className: "orders-select-cell",
    headerClassName: "orders-select-header",
  },
});

const getEditDisabledReason = (
  order: OrdersWidgetRow,
  editAvailable: boolean,
): string | undefined => {
  if (order.isCancelling) {
    return "Cancellation is in progress for this order.";
  }
  if (order.isEditing) {
    return "Replacement submission is in progress for this order.";
  }
  if (!editAvailable) {
    return "Editing is unavailable on this surface.";
  }
  return order.canEdit ? undefined : order.editDisabledReason;
};

const getCancelDisabledReason = (
  order: OrdersWidgetRow,
): string | undefined => {
  if (order.isEditing) {
    return "Replacement submission is in progress for this order.";
  }
  if (order.isCancelling) {
    return "Cancellation is in progress for this order.";
  }
  return order.canCancel ? undefined : order.cancelDisabledReason;
};

const OrderActionIconButton = ({
  label,
  color,
  disabledReason,
  onClick,
  children,
}: {
  label: string;
  color?: "gray" | "red";
  disabledReason?: string;
  onClick: (origin: HTMLButtonElement) => void;
  children: ReactNode;
}) => {
  const disabledReasonId = useId();
  const button = (
    <IconButton
      type="button"
      size="1"
      variant="surface"
      color={color ?? "gray"}
      disabled={Boolean(disabledReason)}
      aria-label={disabledReason ? undefined : label}
      aria-hidden={disabledReason ? true : undefined}
      tabIndex={disabledReason ? -1 : undefined}
      onClick={(event) => onClick(event.currentTarget)}
    >
      {children}
    </IconButton>
  );

  return disabledReason ? (
    <>
      <Tooltip content={disabledReason}>
        {/* biome-ignore lint/a11y/useSemanticElements: a real disabled button cannot trigger the radix tooltip, so this focusable disabled-button proxy wraps it */}
        <span
          role="button"
          tabIndex={0}
          aria-disabled="true"
          aria-label={label}
          aria-describedby={disabledReasonId}
          className="inline-flex cursor-not-allowed"
        >
          {button}
        </span>
      </Tooltip>
      <VisuallyHidden id={disabledReasonId}>{disabledReason}</VisuallyHidden>
    </>
  ) : (
    button
  );
};

const createActionColumn = (
  onCancel?: (orderId: string) => void,
  onEdit?: (orderId: string, origin: HTMLButtonElement) => void,
): OrdersColumn => ({
  accessorKey: "action",
  header: "Action",
  cell: ({ row }) => {
    if (row.original.isLegRow) {
      return null;
    }

    const order = row.original;
    const editLabel = `${order.isEditing ? "Editing" : "Edit"} ${order.symbol} order ${order.id}`;
    const cancelLabel = `${order.isCancelling ? "Cancelling" : "Cancel"} ${order.symbol} order ${order.id}`;

    return (
      <Flex
        align="center"
        justify="center"
        gap="1"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <OrderActionIconButton
          label={editLabel}
          disabledReason={getEditDisabledReason(order, Boolean(onEdit))}
          onClick={(origin) => onEdit?.(order.id, origin)}
        >
          <PencilSimpleIcon aria-hidden size={14} />
        </OrderActionIconButton>
        <OrderActionIconButton
          label={cancelLabel}
          color="red"
          disabledReason={getCancelDisabledReason(order)}
          onClick={() => onCancel?.(order.id)}
        >
          <XCircleIcon aria-hidden size={14} />
        </OrderActionIconButton>
      </Flex>
    );
  },
  enableSorting: false,
  meta: {
    align: "center",
    width: "5.5rem",
    sticky: "end",
    stickyOffset: "0",
    reorderable: false,
    className: "orders-actions-cell",
    headerClassName: "orders-actions-header",
  },
});

const createSymbolColumn = (hasSelection: boolean): OrdersColumn => ({
  accessorKey: "symbol",
  header: "Symbol",
  cell: ({ row }) => {
    const value = row.original.symbol;
    const isParent = row.original.legCount > 1 && !row.original.isLegRow;
    const isLeg = row.original.isLegRow;

    if (isLeg) {
      const ratio =
        row.original.ratioLabel &&
        row.original.ratioLabel !== ORDERS_UNAVAILABLE_VALUE
          ? `${row.original.ratioLabel}× `
          : "";
      return (
        <Flex
          align="center"
          gap="2"
          className="orders-symbol orders-symbol--leg"
        >
          <Text size="1" color="gray" aria-hidden>
            ↳
          </Text>
          <Text
            weight="bold"
            size="1"
            className="orders-symbol-ticker atd-numeric"
            title={row.original.rawSymbol}
          >
            {ratio}
            {value}
          </Text>
        </Flex>
      );
    }

    return (
      <Flex align="center" gap="2" className="orders-symbol">
        {value === ORDERS_UNAVAILABLE_VALUE ? (
          <Text
            color="gray"
            size="2"
            className="orders-symbol-ticker"
            aria-label="Symbol unavailable"
          >
            {value}
          </Text>
        ) : (
          <Text weight="bold" size="2" className="orders-symbol-ticker">
            {value}
          </Text>
        )}
        {isParent ? (
          <Badge color="purple" variant="soft" size="1">
            MLEG
          </Badge>
        ) : null}
      </Flex>
    );
  },
  enableSorting: true,
  sortFn: compareOrdersColumn,
  meta: {
    width: "14rem",
    sticky: "start",
    stickyOffset: hasSelection ? SELECT_COLUMN_WIDTH : "0",
    className: "orders-symbol-cell",
    headerClassName: "orders-symbol-header",
    reorderable: false,
  },
});

const createNameColumn = (): OrdersColumn => ({
  accessorKey: "name",
  header: "Name",
  cell: ({ row }) => {
    const value = row.original.name;
    const isParent = row.original.legCount > 1 && !row.original.isLegRow;
    const isLeg = row.original.isLegRow;

    if (value === ORDERS_UNAVAILABLE_VALUE) {
      return (
        <Text
          color="gray"
          size={isLeg ? "1" : "2"}
          aria-label="Name unavailable"
        >
          {ORDERS_UNAVAILABLE_VALUE}
        </Text>
      );
    }

    if (isParent) {
      return (
        <Text size="2" color="gray" weight="medium">
          {value}
        </Text>
      );
    }

    if (isLeg) {
      return (
        <Text size="1" color="gray" className="whitespace-nowrap">
          {value}
        </Text>
      );
    }

    return (
      <Text size="2" color="gray" truncate>
        {value}
      </Text>
    );
  },
  enableSorting: true,
  sortFn: compareOrdersColumn,
  meta: { width: "17rem" },
});

const ORDERS_DATA_COLUMNS: DataTableColumns<OrdersWidgetRow> = [
  createTextColumn(
    "submittedAt",
    "Submitted At",
    "Submitted at",
    {
      numeric: true,
      width: "12.5rem",
    },
    {
      color: "gray",
      size: "1",
      enableSorting: true,
      sortDescFirst: true,
    },
  ),
  createTextColumn(
    "createdAt",
    "Created At",
    "Created at",
    {
      numeric: true,
      width: "12.5rem",
    },
    { color: "gray", size: "1" },
  ),
  createSymbolColumn(false),
  createNameColumn(),
  {
    accessorKey: "side",
    header: "Side",
    cell: (info) =>
      info.getValue<string>() === ORDERS_UNAVAILABLE_VALUE ? (
        <TextValue value={info.getValue<string>()} label="Side" />
      ) : (
        <Badge color={info.row.original.sideColor} variant="soft" size="1">
          {info.getValue<string>()}
        </Badge>
      ),
    enableSorting: true,
    sortFn: compareOrdersColumn,
    meta: { width: "5.5rem" },
  },
  createTextColumn(
    "orderType",
    "Type",
    "Order type",
    { width: "7rem" },
    {
      color: "gray",
      size: "2",
    },
  ),
  createTextColumn("quantity", "Qty", "Quantity", {
    align: "end",
    numeric: true,
    width: "6.5rem",
  }),
  createTextColumn("filledQuantity", "Filled", "Filled quantity", {
    align: "end",
    numeric: true,
    width: "6.5rem",
  }),
  createTextColumn("limitPrice", "Limit", "Limit price", {
    align: "end",
    numeric: true,
    width: "7rem",
  }),
  {
    accessorKey: "status",
    header: "Status",
    cell: (info) =>
      info.getValue<string>() === ORDERS_UNAVAILABLE_VALUE ? (
        <TextValue value={info.getValue<string>()} label="Status" />
      ) : (
        <Badge color={info.row.original.statusColor} variant="soft" size="1">
          {info.getValue<string>()}
        </Badge>
      ),
    enableSorting: true,
    sortFn: compareOrdersColumn,
    meta: { width: "8rem" },
  },
  createTextColumn("stopPrice", "Stop Price", "Stop price", {
    align: "end",
    numeric: true,
    width: "9rem",
  }),
  createTextColumn("trailPrice", "Trail Price", "Trail price", {
    align: "end",
    numeric: true,
    width: "9rem",
  }),
  createTextColumn("trailPercent", "Trail Percent", "Trail percent", {
    align: "end",
    numeric: true,
    width: "9rem",
  }),
  createTextColumn("extendedHours", "Extended Hours", "Extended hours", {
    width: "9rem",
  }),
  createTextColumn("clientOrderId", "Client Order ID", "Client order ID", {
    width: "14rem",
  }),
  createTextColumn(
    "orderClass",
    "Order Class",
    "Order class",
    { width: "8rem" },
    { color: "gray", size: "2" },
  ),
  {
    accessorKey: "algorithm",
    header: "Algorithm",
    cell: (info) => {
      const value = info.getValue<string>();
      if (value === ORDERS_UNAVAILABLE_VALUE) {
        return <TextValue value={value} label="Algorithm" />;
      }
      return (
        <Badge color="blue" variant="soft" size="1">
          {value}
        </Badge>
      );
    },
    enableSorting: true,
    sortFn: compareOrdersColumn,
    meta: { width: "8rem" },
  },
  createTextColumn(
    "destination",
    "Destination",
    "Destination",
    { width: "9rem" },
    { color: "gray", size: "2" },
  ),
  createTextColumn(
    "startTime",
    "Start Time",
    "Start time",
    {
      numeric: true,
      width: "12.5rem",
    },
    { color: "gray", size: "1" },
  ),
  createTextColumn(
    "endTime",
    "End Time",
    "End time",
    {
      numeric: true,
      width: "12.5rem",
    },
    { color: "gray", size: "1" },
  ),
  createTextColumn("maxPercentage", "Max Percentage", "Max percentage", {
    align: "end",
    numeric: true,
    width: "9.5rem",
  }),
  createTextColumn("notional", "Notional", "Notional", {
    align: "end",
    numeric: true,
    width: "10rem",
  }),
  createTextColumn(
    "timeInForce",
    "Time-in-Force",
    "Time-in-force",
    { width: "9rem" },
    { color: "gray", size: "2" },
  ),
  createTextColumn(
    "filledAtAveragePrice",
    "Filled at Avg Price",
    "Filled quantity and average price",
    { align: "end", numeric: true, width: "14rem" },
    { color: "gray", size: "2" },
  ),
  createTextColumn(
    "source",
    "Source",
    "Source",
    { width: "8rem" },
    { color: "gray", size: "2" },
  ),
  createTextColumn(
    "positionIntent",
    "Position Intent",
    "Position intent",
    { width: "10rem" },
    { color: "gray", size: "2" },
  ),
  createTextColumn(
    "markets",
    "Markets",
    "Markets",
    { width: "7rem" },
    { color: "gray", size: "2" },
  ),
  createTextColumn(
    "currency",
    "Currency",
    "Currency",
    { width: "7rem" },
    { color: "gray", size: "2" },
  ),
];

const replaceSymbolColumn = (
  columns: DataTableColumns<OrdersWidgetRow>,
  hasSelection: boolean,
): DataTableColumns<OrdersWidgetRow> =>
  columns.map((column) =>
    "accessorKey" in column && column.accessorKey === "symbol"
      ? createSymbolColumn(hasSelection)
      : column,
  );

export const createOrdersWidgetColumns = (
  callbacks: OrdersWidgetColumnCallbacks = {},
): DataTableColumns<OrdersWidgetRow> => [
  ...(callbacks.selection ? [createSelectionColumn(callbacks.selection)] : []),
  ...replaceSymbolColumn(ORDERS_DATA_COLUMNS, Boolean(callbacks.selection)),
  ...(callbacks.onCancel || callbacks.onEdit
    ? [createActionColumn(callbacks.onCancel, callbacks.onEdit)]
    : []),
];

export const ORDERS_WIDGET_COLUMNS = createOrdersWidgetColumns();

export const orderOrdersWidgetColumnOptions = (
  options: readonly OrdersWidgetColumnOption[],
  columnOrder: readonly OrdersWidgetColumnId[],
): OrdersWidgetColumnOption[] => {
  const optionsById = new Map(options.map((option) => [option.id, option]));
  return columnOrder.flatMap((columnId) => {
    const option = optionsById.get(columnId);
    return option ? [option] : [];
  });
};

const getOrdersWidgetColumnAccessorKey = (
  column: DataTableColumns<OrdersWidgetRow>[number],
): string | undefined =>
  "accessorKey" in column && typeof column.accessorKey === "string"
    ? column.accessorKey
    : undefined;

export const selectVisibleOrdersWidgetColumns = (
  visibleColumnIds: readonly OrdersWidgetColumnId[],
  columns: DataTableColumns<OrdersWidgetRow> = ORDERS_WIDGET_COLUMNS,
): DataTableColumns<OrdersWidgetRow> => {
  const selectColumn = columns.find((column) => column.id === "select");
  const columnsById = new Map(
    columns.flatMap((column) => {
      const accessorKey = getOrdersWidgetColumnAccessorKey(column);
      return accessorKey && isOrdersWidgetColumnId(accessorKey)
        ? [[accessorKey, column] as const]
        : [];
    }),
  );
  const visibleWithoutAction = visibleColumnIds.filter(
    (columnId) => columnId !== "action",
  );
  const orderedColumns = visibleWithoutAction.flatMap((columnId) => {
    const column = columnsById.get(columnId);
    return column ? [column] : [];
  });
  const actionColumn = visibleColumnIds.includes("action")
    ? columnsById.get("action")
    : undefined;

  return [
    ...(selectColumn ? [selectColumn] : []),
    ...orderedColumns,
    ...(actionColumn ? [actionColumn] : []),
  ];
};

const MINIMUM_ORDERS_TABLE_WIDTH_REM = 36;
const FALLBACK_COLUMN_WIDTH_REM = 8;

export const getOrdersWidgetTableMinWidth = (
  columns: DataTableColumns<OrdersWidgetRow>,
): string => {
  const configuredWidth = columns.reduce((totalWidth, column) => {
    const width = column.meta?.width;
    const widthInRem =
      typeof width === "string" && width.endsWith("rem")
        ? Number.parseFloat(width)
        : FALLBACK_COLUMN_WIDTH_REM;

    return totalWidth + (Number.isFinite(widthInRem) ? widthInRem : 0);
  }, 0);

  return `${Math.max(configuredWidth, MINIMUM_ORDERS_TABLE_WIDTH_REM)}rem`;
};
